/**
 * Notification Store
 *
 * Zustand store for notification state management.
 * Provides reactive state for the entire application.
 *
 * @module shared/stores/notificationStore
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { toast } from '@/shared/hooks/useToast';
import { registerFamilyScopedReset } from '@/shared/lib/storeReset';
import { notificationService } from '@/shared/services/notificationService';
import type {
  Notification,
  CreateNotificationDTO,
  NotificationStore,
} from '@/shared/types/notification.types';
import { ToastVariant } from '@/shared/types/toast.types';

// Concurrency guard for background refetches (module-level — not rendered
// state, so it never causes loading-skeleton flashes).
let refetchInFlight = false;

/**
 * Surface a notification write failure: store the message and toast the user.
 * Notification writes are pessimistic (service-first), so there is no
 * optimistic state to roll back — the store was never mutated on the failing
 * path.
 */
function surfaceNotificationError(
  error: unknown,
  fallbackMessage: string,
  set: (partial: Partial<NotificationStore>) => void
): void {
  const message = error instanceof Error ? error.message : fallbackMessage;
  set({ error: message });
  toast({
    variant: ToastVariant.Destructive,
    title: 'Грешка при известията',
    description: message,
  });
}

/**
 * Notification store with state and actions
 *
 * @example
 * ```typescript
 * import { useNotificationStore } from '@/shared/stores/notificationStore';
 *
 * // In a component
 * const { notifications, unreadCount, fetchNotifications } = useNotificationStore();
 *
 * // Fetch on mount
 * useEffect(() => {
 *   fetchNotifications();
 * }, [fetchNotifications]);
 *
 * // Get just the unread count for badge
 * const unreadCount = useNotificationStore((state) => state.unreadCount);
 * ```
 */
export const useNotificationStore = create<NotificationStore>((set) => ({
  // ============================================
  // State
  // ============================================
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  isLoaded: false,
  error: null,

  // ============================================
  // Actions
  // ============================================

  /**
   * Fetch all notifications from the service
   */
  fetchNotifications: async () => {
    // Idempotent: skip when already loaded or a fetch is in flight so that
    // remounting the bell / notifications page does not trigger redundant
    // requests. Realtime keeps the list fresh after the first load.
    const { isLoaded, isLoading } = useNotificationStore.getState();
    if (isLoaded || isLoading) return;

    set({ isLoading: true, error: null });
    try {
      const notifications = await notificationService.getNotifications();
      const unreadCount = notifications.filter((n) => !n.isRead).length;
      set({
        notifications,
        unreadCount,
        isLoading: false,
        isLoaded: true,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Грешка при зареждане на известията',
        isLoading: false,
      });
    }
  },

  /**
   * Force a fresh read from the backend, bypassing the `isLoaded` /
   * `isLoading` guards. Used for catch-up after a realtime reconnect and on
   * window focus/online events. Never toggles `isLoading`.
   */
  refetch: async () => {
    if (refetchInFlight) return;
    refetchInFlight = true;
    try {
      const notifications = await notificationService.getNotifications();
      const unreadCount = notifications.filter((n) => !n.isRead).length;
      set({
        notifications,
        unreadCount,
        isLoaded: true,
        error: null,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Грешка при зареждане на известията',
      });
    } finally {
      refetchInFlight = false;
    }
  },

  /**
   * Add a new notification
   */
  addNotification: async (data: CreateNotificationDTO) => {
    try {
      const notification = await notificationService.addNotification(data);
      set((state) => ({
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + 1,
      }));
    } catch (error) {
      surfaceNotificationError(error, 'Грешка при добавяне на известие', set);
    }
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      set((state) => {
        const notification = state.notifications.find((n) => n.id === id);
        const wasUnread = notification && !notification.isRead;

        return {
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, isRead: true } : n
          ),
          unreadCount: wasUnread
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
        };
      });
    } catch (error) {
      surfaceNotificationError(
        error,
        'Грешка при маркиране като прочетено',
        set
      );
    }
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async () => {
    try {
      await notificationService.markAllAsRead();
      set((state) => ({
        notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
        unreadCount: 0,
      }));
    } catch (error) {
      surfaceNotificationError(
        error,
        'Грешка при маркиране на всички като прочетени',
        set
      );
    }
  },

  /**
   * Mark a chat-invite notification as expired (room no longer exists).
   * In-memory only; does not touch the service or unread count.
   */
  markExpired: (id: string) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === id ? { ...n, expired: true } : n
      ),
    }));
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (id: string) => {
    try {
      await notificationService.deleteNotification(id);
      set((state) => {
        const notification = state.notifications.find((n) => n.id === id);
        const wasUnread = notification && !notification.isRead;

        return {
          notifications: state.notifications.filter((n) => n.id !== id),
          unreadCount: wasUnread
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
        };
      });
    } catch (error) {
      surfaceNotificationError(error, 'Грешка при изтриване на известие', set);
    }
  },

  /**
   * Merge a notification received from a realtime stream (P7). Local-only:
   * does NOT call the service. Inserts when unseen, replaces in place when
   * already present (dedup by id), and recomputes the unread count.
   */
  upsertRealtimeNotification: (notification: Notification) => {
    set((state) => {
      const exists = state.notifications.some((n) => n.id === notification.id);
      const notifications = exists
        ? state.notifications.map((n) =>
            n.id === notification.id ? notification : n
          )
        : [notification, ...state.notifications];
      return {
        notifications,
        unreadCount: notifications.filter((n) => !n.isRead).length,
      };
    });
  },

  /**
   * Remove a notification deleted on another device (P7). Local-only: does NOT
   * call the service. Recomputes the unread count.
   */
  removeRealtimeNotification: (id: string) => {
    set((state) => {
      const notifications = state.notifications.filter((n) => n.id !== id);
      return {
        notifications,
        unreadCount: notifications.filter((n) => !n.isRead).length,
      };
    });
  },

  /**
   * Clear all notifications
   */
  clearAll: async () => {
    try {
      await notificationService.clearAll();
      set({
        notifications: [],
        unreadCount: 0,
      });
    } catch (error) {
      surfaceNotificationError(
        error,
        'Грешка при изчистване на известията',
        set
      );
    }
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },
}));

// ============================================
// Selectors
// ============================================

/**
 * Selector for unread count (for badge display)
 *
 * @example
 * ```typescript
 * const unreadCount = useNotificationStore(selectUnreadCount);
 * ```
 */
export const selectUnreadCount = (state: NotificationStore): number =>
  state.unreadCount;

/**
 * Hook for recent notifications (last 5)
 * Uses useShallow to prevent infinite re-renders from array creation
 *
 * @example
 * ```typescript
 * const recentNotifications = useRecentNotifications();
 * ```
 */
export const useRecentNotifications = (): readonly Notification[] =>
  useNotificationStore(useShallow((state) => state.notifications.slice(0, 5)));

/**
 * Hook for unread notifications
 * Uses useShallow to prevent infinite re-renders from array creation
 *
 * @example
 * ```typescript
 * const unreadNotifications = useUnreadNotifications();
 * ```
 */
export const useUnreadNotifications = (): readonly Notification[] =>
  useNotificationStore(
    useShallow((state) => state.notifications.filter((n) => !n.isRead))
  );

/**
 * Stable sort that surfaces unread notifications above read ones while
 * preserving the existing (newest-first) relative order within each group.
 *
 * @example
 * ```typescript
 * const ordered = sortNotificationsUnreadFirst(notifications);
 * ```
 */
export const sortNotificationsUnreadFirst = (
  list: readonly Notification[]
): readonly Notification[] =>
  [...list].sort((a, b) => Number(a.isRead) - Number(b.isRead));

// Clear notification data whenever the active family changes; the next fetch
// re-loads within the new scope (isLoaded=false re-arms fetchNotifications).
registerFamilyScopedReset(() => {
  useNotificationStore.setState({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    isLoaded: false,
    error: null,
  });
});
